import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { ShipmentPackageDto } from "@insite/client-framework/Types/ApiModels";
import Clickable from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import X from "@insite/mobius/Icons/X";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import StatusLableAndHeadingOrderDetailTopStatus from "@WhiteCap/Components/StatusLableAndHeadingOrderDetailTopStatus";
import * as React from "react";
import { RefObject, useEffect, useRef } from "react";
import { css } from "styled-components";

interface OwnProps {
    onHandleClose: (event: React.SyntheticEvent) => void;
    orderNumber?: string;
    isModalIsOpen?: boolean;
    shipmentPackages?: ShipmentPackageDto[] | null;
    properties?: { [key: string]: string };
    parentRef?: RefObject<HTMLDivElement>;
}

export interface MultipleDeliveryAndStatustyles {
    parentWrapper?: InjectableCss;
    topWrapper?: InjectableCss;
    deliveryListWrapper?: InjectableCss;
    deliveryListContainer?: InjectableCss;
    deliveryDetailstWrapper?: InjectableCss;
    statusWrapper?: InjectableCss;
    closeIcon?: IconPresentationProps;
    closebutton?: InjectableCss;
    orderNumber?: TypographyPresentationProps;
    deliveryHeading?: TypographyPresentationProps;
    deliveryType?: TypographyPresentationProps;
}

export const multipleDeliveryAndStatustyles: MultipleDeliveryAndStatustyles = {
    parentWrapper: {
        css: css`
            position: fixed;
            background-color: white;
            box-shadow: 0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23);
            z-index: ${getProp("theme.zIndex.menu")};
            display: flex;
            flex-direction: column;
            padding: 20px;
            transition: opacity 0.3s ease-in-out;
            @media screen and (max-width: 340px) {
                overflow-x: auto;
                width: 90vw;
                white-space: nowrap;
                padding: 20px 15px;
            }
        `,
    },
    topWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            padding-bottom: 10px;
        `,
    },
    deliveryListContainer: {
        css: css`
            max-height: 235px;
            overflow-y: auto;
            padding-right: 2px;
        `,
    },
    deliveryListWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 5px 8px;
            width: 100%;
        `,
    },
    deliveryDetailstWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 1px;
            margin-right: 18px;
            @media screen and (max-width: 340px) {
                margin-right: 15px;
            }
            @media screen and (max-width: 320px) {
                margin-right: 5px;
            }
        `,
    },
    statusWrapper: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    orderNumber: {
        css: css`
            font-size: 16px;
            font-weight: 600;
        `,
    },
    deliveryHeading: {
        css: css`
            font-size: 12px;
            font-weight: 500;
            max-width: 120px;
            min-width: 120px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            text-align: left;
        `,
    },
    deliveryType: {
        css: css`
            font-size: 12px;
            max-width: 120px;
            min-width: 120px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            text-align: left;
        `,
    },
};

const styles = multipleDeliveryAndStatustyles;

type Props = OwnProps;

const MultipleDeliveryAndStatusComponent: React.FC<Props> = ({
    onHandleClose,
    orderNumber,
    isModalIsOpen,
    shipmentPackages,
    properties,
    parentRef,
}) => {
    if (!isModalIsOpen) {
        return null;
    }

    const modalRef = useRef<HTMLDivElement>(null);
    const hasClosed = useRef(false);
    const isMobile = window.innerWidth <= 768;

    const updateModalPosition = (shouldAdjustPosition = false) => {
        if (!parentRef?.current || !modalRef.current) {
            return;
        }

        const parentRect = parentRef.current.getBoundingClientRect();
        const modal = modalRef.current;
        const modalRect = modal.getBoundingClientRect();

        // Calculate the initial position of the modal
        let top = parentRect.top - modalRect.height;
        let left = parentRect.left + parentRect.width / 2 - modalRect.width / 2;

        // Ensure the modal does not overflow to the right on desktop on scroll or on resize
        if (!isMobile && left + modalRect.width > window.innerWidth) {
            left = window.innerWidth - modalRect.width; // Move the modal to the left
        }

        // Apply fixed position and top to the modal
        modal.style.position = "fixed";
        modal.style.top = `${top}px`;

        if (!isMobile) {
            modal.style.left = `${left}px`;
        }

        if (!shouldAdjustPosition) {
            handleInitialOverflow(modal, left, top, modalRect.width);
        }
    };

    const handleInitialOverflow = (modal: HTMLElement, left: number, top: number, modalWidth: number) => {
        const topOverflowAmount = Math.abs(top); // Amount the modal overflows at the top
        const modalRight = left + modalWidth;

        if (isMobile) {
            // Mobile: Center horizontally and adjust top overflow if needed
            modal.style.left = "50%";
            modal.style.transform = `translateX(-50%) ${
                top < 0 ? `translateY(${topOverflowAmount}px)` : "translateY(0px)"
            }`;
        } else {
            // Desktop: Adjust for top and right overflow
            let transform = "";

            if (modalRight > window.innerWidth) {
                const overflowAmountX = modalRight - window.innerWidth; // Calculate horizontal overflow
                transform += `translateX(-${overflowAmountX}px)`;
            }

            modal.style.transform = transform.trim();
        }
    };
    useEffect(() => {
        if (isModalIsOpen) {
            updateModalPosition();
            const scrollableContainer = document.querySelector('[data-test-selector="orderHistoryTable"]');
            const handleScroll = () => updateModalPosition(true);
            const handleResize = () => updateModalPosition(true);

            if (scrollableContainer) {
                scrollableContainer.addEventListener("scroll", handleScroll);
            }
            window.addEventListener("scroll", handleScroll);
            window.addEventListener("resize", handleResize);

            return () => {
                if (scrollableContainer) {
                    scrollableContainer.removeEventListener("scroll", handleScroll);
                }
                window.removeEventListener("scroll", handleScroll);
                window.removeEventListener("resize", handleResize);
            };
        }
    }, [isModalIsOpen, parentRef]);

    useEffect(() => {
        const handleClickOutside = (event: PointerEvent) => {
            if (modalRef?.current && !modalRef?.current.contains(event.target as Node)) {
                if (hasClosed.current) {
                    return;
                }
                hasClosed.current = true;
                onHandleClose(event as unknown as React.SyntheticEvent);
            }
        };

        document.addEventListener("pointerdown", handleClickOutside);

        return () => {
            document.removeEventListener("pointerdown", handleClickOutside);
            hasClosed.current = false;
        };
    }, [onHandleClose]);

    useEffect(() => {
        if (isModalIsOpen) {
            hasClosed.current = false;
        }
    }, [isModalIsOpen]);

    const shipmentStatusByDeliveryId = properties?.shipmentStatus ? JSON.parse(properties.shipmentStatus) : {};

    const deliveryAndstatusList = React.useMemo(() => {
        const groupedList: {
            [key: string]: { deliveryId: string; shippingMethod: string | undefined; status: string };
        } = {};

        if (properties?.realtimeOrderStatus) {
            const realtimeOrderStatus: { deliveryId: string; status: string }[] = JSON.parse(
                properties.realtimeOrderStatus,
            );

            realtimeOrderStatus.forEach(({ deliveryId, status }) => {
                const matchedShipmentPackage = shipmentPackages?.find(
                    shipmentPackage => shipmentPackage.packageNumber.split("-")[0] === deliveryId,
                );

                // Use deliveryId as the key and overwrite if it already exists
                groupedList[deliveryId] = {
                    deliveryId,
                    shippingMethod: matchedShipmentPackage?.carrier,
                    status: status || shipmentStatusByDeliveryId[deliveryId], // Use shipmentStatus if available, otherwise fallback to realtimeOrderStatus
                };
            });
        } else if (shipmentPackages) {
            shipmentPackages.forEach(shipmentPackage => {
                const deliveryId = shipmentPackage.packageNumber.split("-")[0];
                // Use deliveryId as the key and overwrite if it already exists
                groupedList[deliveryId] = {
                    deliveryId,
                    shippingMethod: shipmentPackage.carrier,
                    status: shipmentStatusByDeliveryId[deliveryId],
                };
            });
        }
        return Object.values(groupedList);
    }, [properties, shipmentPackages, shipmentStatusByDeliveryId]);

    return (
        <StyledWrapper ref={modalRef} {...styles.parentWrapper} onClick={e => e.stopPropagation()}>
            <StyledWrapper {...styles.topWrapper}>
                {orderNumber && <Typography {...styles.orderNumber}>{`Order #${orderNumber}`}</Typography>}
                <Clickable aria-labelledby="close-modal" onClick={onHandleClose}>
                    <Icon {...styles.closeIcon} src={X} />
                </Clickable>
            </StyledWrapper>
            <StyledWrapper {...styles.deliveryListContainer}>
                {deliveryAndstatusList.map((item, index) => (
                    <StyledWrapper key={item.deliveryId} {...styles.deliveryListWrapper}>
                        <StyledWrapper {...styles.deliveryDetailstWrapper}>
                            <Typography {...styles.deliveryHeading}>
                                {`Delivery ${index + 1}/${deliveryAndstatusList.length}`}
                            </Typography>
                            <Typography {...styles.deliveryType}>{item.shippingMethod}</Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.statusWrapper}>
                            <StatusLableAndHeadingOrderDetailTopStatus
                                heading={translate("Status")}
                                text={item.status}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default MultipleDeliveryAndStatusComponent;
